import { Button, Flex, Select, Text, TextField } from '@radix-ui/themes'
import { BudgetGoalSchema, createBudgetGoalSchema, CreateBudgetGoalSchema, goalSchema } from '../schemas'
import { listSiteMetrics } from '~/features/dashboarding/service'
import { useQuery } from '@tanstack/react-query'
import { listSiteWaterStations } from '~/features/stations/service'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { GOAL_MEASURE_UNIT } from '../constants'
import ResolutionSelector from '~/components/ResolutionSelector'
import { orderBy } from 'lodash'
import { FieldWrapper } from '~/components/FormFieldWrapper'
import DatePickerField from '~/components/DatePickerField'

type CommonProps = {
  goal: BudgetGoalSchema | null
  defaultData?: Partial<CreateBudgetGoalSchema> | null
  isPending: boolean
  siteID: string
}

type BudgetGoalForm = CommonProps & (CreateBudgetGoalFormProps | UpdateBudgetGoalFormProps)

type ViewOption = BudgetGoalSchema['measure']

const BudgetGoalForm = ({ siteID, isPending, goal, onSubmit, defaultData = {} }: BudgetGoalForm) => {
  const { t } = useTranslation('sites')
  const schema = goal ? goalSchema : createBudgetGoalSchema

  const { data: metrics = [] } = useQuery({
    queryKey: ['site-metrics', siteID],
    queryFn: () => listSiteMetrics(siteID).then((metrics) => orderBy(metrics, 'name', 'asc'))
  })

  const { data: stations = [] } = useQuery({
    queryKey: ['site-stations', siteID],
    queryFn: () => listSiteWaterStations({ siteId: siteID }).then((stations) => orderBy(stations, 'name', 'asc'))
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isDirty },
    setValue,
    watch
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: goal || { measure: 'currency', resolution: 'month', ...defaultData },
    mode: 'onChange'
  })

  const [startDate, endDate, stationID, metricID] = watch(['startDate', 'endDate', 'stationID', 'metricID'])

  const formatViewAsLabel = (v: ViewOption) => `${t(`budget.chart.viewAs.${v}`)} ${GOAL_MEASURE_UNIT[v]}`

  const handleScopeChanged = (value: string) => {
    if (stations.some((station) => station.id === value)) {
      setValue('stationID', value, { shouldValidate: true, shouldDirty: true })
      setValue('metricID', undefined, { shouldValidate: true, shouldDirty: true })
    } else if (metrics.some((metric) => metric.id === value)) {
      setValue('metricID', value, { shouldValidate: true, shouldDirty: true })
      setValue('stationID', undefined, { shouldValidate: true, shouldDirty: true })
    }
  }

  const selectedStationOrMetric = useMemo(
    () => stations.find((station) => station.id === stationID) || metrics.find((metric) => metric.id === metricID),
    [stations, metrics, stationID, metricID]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex gap={'8'} mt={'4'}>
        <Flex direction={'column'} gap="5" flexGrow={'1'} width={'43%'}>
          <Flex direction={'column'} gap="2">
            <Text size="2" weight={'medium'}>
              {t('budget.objectives.form.period')}
            </Text>
            <Text size="2" as="p" color={'gray'}>
              {t('budget.objectives.form.periodDescription')}
            </Text>
            <Flex gap={'3'} width={'full'} className="[&>*]:flex-grow">
              <FieldWrapper label={t('startDate', { ns: 'common' })}>
                <DatePickerField
                  value={startDate}
                  onChange={(from) => setValue('startDate', from, { shouldValidate: true, shouldDirty: true })}
                />
              </FieldWrapper>
              <FieldWrapper label={t('endDate', { ns: 'common' })}>
                <DatePickerField
                  value={endDate}
                  onChange={(to) => setValue('endDate', to, { shouldValidate: true, shouldDirty: true })}
                />
              </FieldWrapper>
            </Flex>
          </Flex>
          <Flex direction={'column'} gap="2">
            <Text size="2" weight={'medium'}>
              {t('budget.objectives.form.resolution')}
            </Text>
            <Text size="2" as="p" color={'gray'}>
              {t('budget.objectives.form.resolutionDescription')}
            </Text>
            <Controller
              control={control}
              name="resolution"
              render={({ field }) => (
                <ResolutionSelector
                  omit={['hour']}
                  color="mint"
                  size={'2'}
                  onChange={field.onChange}
                  defaultResolution={field.value}
                />
              )}
            />
          </Flex>
        </Flex>
        <Flex direction={'column'} gap="5" flexGrow={'1'}>
          <Flex direction={'column'} gap="2">
            <Text size="2" weight={'medium'}>
              {t('budget.objectives.form.scope')}
            </Text>
            <Text size="2" as="p" color={'gray'}>
              {t('budget.objectives.form.scopeDescription')}
            </Text>
            <Select.Root
              disabled={!!defaultData?.metricID || !!defaultData?.stationID}
              onValueChange={handleScopeChanged}
              value={stationID || metricID}
            >
              <Select.Trigger placeholder={t('budget.objectives.form.scopePlaceholder')}>
                <Text size="2">
                  {selectedStationOrMetric?.name ?? t('budget.objectives.form.scopePlaceholder')}
                </Text>
              </Select.Trigger>
              <Select.Content variant="soft">
                <Select.Group>
                  <Select.Label>{t('budget.objectives.form.stations')}</Select.Label>
                  {stations.map((station) => (
                    <Select.Item key={station.id} value={station.id}>
                      {station.name}
                    </Select.Item>
                  ))}
                </Select.Group>
                <Select.Separator />
                <Select.Group>
                  <Select.Label>{t('budget.objectives.form.metrics')}</Select.Label>
                  {metrics.map((metric) => (
                    <Select.Item key={metric.id} value={metric.id}>
                      {metric.name}
                    </Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
          <Flex direction={'column'} gap="2">
            <Text size="2" weight={'medium'}>
              {t('budget.objectives.form.budget')}
            </Text>
            <Text size="2" as="p" color={'gray'}>
              {t('budget.objectives.form.budgetDescription')}
            </Text>
            <TextField.Root
              placeholder={t('budget.objectives.form.numberPlaceholder')}
              type="number"
              min={0}
              step={0.01}
              {...register('goal', { valueAsNumber: true })}
            />
          </Flex>
          <Flex direction={'column'} gap="2">
            <Text size="2" weight={'medium'}>
              {t('budget.objectives.form.measure')}
            </Text>
            <Text size="2" as="p" color={'gray'}>
              {t('budget.objectives.form.measureDescription')}
            </Text>
            <Controller
              control={control}
              name="measure"
              render={({ field }) => (
                <Select.Root {...field} onValueChange={(v: ViewOption) => field.onChange(v)}>
                  <Select.Trigger>{formatViewAsLabel(field.value)}</Select.Trigger>
                  <Select.Content variant="soft">
                    {Object.keys(GOAL_MEASURE_UNIT).map((viewOption) => (
                      <Select.Item key={viewOption} value={viewOption}>
                        {formatViewAsLabel(viewOption as ViewOption)}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              )}
            />
          </Flex>
          <Button type="submit" size={'3'} loading={isPending} disabled={!isValid || !isDirty} className="ml-auto">
            {goal ? t('budget.objectives.form.buttons.update') : t('budget.objectives.form.buttons.create')}
          </Button>
        </Flex>
      </Flex>
    </form>
  )
}

type CreateBudgetGoalFormProps = {
  onSubmit: (data: CreateBudgetGoalSchema) => void
  defaultData?: Partial<CreateBudgetGoalSchema>
}

export const CreateBudgetGoalForm = (props: CreateBudgetGoalFormProps & CommonProps) => {
  return <BudgetGoalForm {...props} />
}

type UpdateBudgetGoalFormProps = {
  goal: BudgetGoalSchema
  onSubmit: (data: BudgetGoalSchema) => void
}

export const UpdateBudgetGoalForm = (props: UpdateBudgetGoalFormProps & CommonProps) => {
  return <BudgetGoalForm {...props} />
}
